// Lightweight i18n for the SpotDrop landing page.
// - No dependency. Just React Context + a `t(key)` helper.
// - English is the canonical source of truth AND the fallback for any missing key.
// - Language choice persists in localStorage; no browser/geo auto-detection.
// - Not applied to /app or /admin (see LangProvider scope).

import React from "react";
import en from "./en";
import mt from "./mt";
import it from "./it";

const DICTS = { en, mt, it };
export const SUPPORTED = ["en", "mt", "it"];
const STORAGE_KEY = "spotdrop_lang";
const DEFAULT_LANG = "en";

function getStoredLang() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SUPPORTED.includes(v)) return v;
  } catch {
    /* ignore */
  }
  return DEFAULT_LANG;
}

function setStoredLang(lang) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
}

// Look up a dotted key against a nested dictionary.
function lookup(dict, key) {
  if (!dict || !key) return undefined;
  const parts = key.split(".");
  let cur = dict;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[p];
  }
  return typeof cur === "string" ? cur : undefined;
}

const LangCtx = React.createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key, fallback) => fallback ?? key,
});

export function LangProvider({ children }) {
  const [lang, setLangState] = React.useState(() => getStoredLang());

  // Reflect on <html lang="…"> for accessibility.
  React.useEffect(() => {
    try {
      document.documentElement.lang = lang;
    } catch {
      /* ignore */
    }
  }, [lang]);

  const setLang = React.useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setStoredLang(next);
    setLangState(next);
  }, []);

  const t = React.useCallback(
    (key, fallback) => {
      const primary = lookup(DICTS[lang], key);
      if (primary != null) return primary;
      const en = lookup(DICTS[DEFAULT_LANG], key);
      if (en != null) return en;
      return fallback ?? key;
    },
    [lang]
  );

  const value = React.useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export function useLang() {
  return React.useContext(LangCtx);
}
